import type { configType } from "../types";
import {
  formatMetricValue,
  resolveThresholdColor,
  computeDonutData,
} from "./computeMetric";

interface DonutRingProps {
  numeratorRows: Record<string, unknown>[];
  denominatorRows: Record<string, unknown>[] | null;
  config: configType;
  isLoading: boolean;
}

const SIZE = 120;
const STROKE = 12;
const R = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * R;
const CENTER = SIZE / 2;

const TRACK_COLOR = "#D5DADC";
const FILL_COLORS = {
  default: "#64BDE1",
  warn: "#E5AB5F",
  danger: "#991962",
} as const;

export const DonutRing = ({
  numeratorRows,
  denominatorRows,
  config,
  isLoading,
}: DonutRingProps) => {
  const mc = config.metricCard!;
  const { numerator, denominator, ratio } = computeDonutData(
    numeratorRows,
    denominatorRows,
    mc,
  );

  // Without plot filters the numerator = denominator and the ratio is always 100%.
  // In that case show the raw value rather than a useless "100%" percentage.
  const hasFilters = config.hasPlotFilters;
  const showRawValue = !hasFilters && mc.denominatorSource !== "fixed";

  const colorKey = resolveThresholdColor(
    showRawValue ? numerator : ratio * 100,
    mc,
  );
  const fillColor = FILL_COLORS[colorKey];

  const clampedRatio = showRawValue ? 1 : Math.min(1, Math.max(0, ratio));
  const dashOffset = CIRCUMFERENCE * (1 - clampedRatio);

  const fractionLabel = showRawValue
    ? `${mc.aggregation === "count" || mc.aggregation === "count-distinct" ? "rows" : (mc.valueColumn ?? "")}`
    : `${formatMetricValue(numerator)}${mc.unit ? ` ${mc.unit}` : ""} / ${formatMetricValue(denominator)}`;

  return (
    <div className="flex flex-col w-full p-4 gap-3">
      <p className="text-[13px] font-semibold text-[#556C75] leading-tight pr-7 truncate">
        {config.labels.title || "Metric"}
      </p>
      {config.labels.subtitle && (
        <p className="text-[11px] text-[#8C8C8C] leading-snug -mt-2 truncate">
          {config.labels.subtitle}
        </p>
      )}

      <div className="flex justify-center items-center">
        {isLoading ? (
          <div
            className="rounded-full bg-[#EFF1F2] animate-pulse"
            style={{ width: SIZE, height: SIZE }}
          />
        ) : (
          <svg
            width={SIZE}
            height={SIZE}
            viewBox={`0 0 ${SIZE} ${SIZE}`}
            aria-label={`${Math.round(ratio * 100)}%`}
          >
            {/* Track + fill arc, rotated so progress starts from top */}
            <g transform={`rotate(-90, ${CENTER}, ${CENTER})`}>
              <circle
                cx={CENTER}
                cy={CENTER}
                r={R}
                fill="none"
                stroke={TRACK_COLOR}
                strokeWidth={STROKE}
              />
              <circle
                cx={CENTER}
                cy={CENTER}
                r={R}
                fill="none"
                stroke={fillColor}
                strokeWidth={STROKE}
                strokeDasharray={CIRCUMFERENCE}
                strokeDashoffset={dashOffset}
                strokeLinecap="round"
                style={{ transition: "stroke-dashoffset 0.6s ease" }}
              />
            </g>
            {/* Center label: raw value (no filters) or percentage (with filters) */}
            <text
              x={CENTER}
              y={showRawValue ? CENTER - 2 : CENTER - 7}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={showRawValue ? 20 : 22}
              fontWeight={600}
              fill={fillColor}
              fontFamily="Work Sans, sans-serif"
            >
              {showRawValue
                ? `${formatMetricValue(numerator)}${mc.unit ? ` ${mc.unit}` : ""}`
                : `${Math.round(ratio * 100)}%`}
            </text>
            {/* Sub-label */}
            <text
              x={CENTER}
              y={showRawValue ? CENTER + 14 : CENTER + 13}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={10}
              fill="#556C75"
              fontFamily="Work Sans, sans-serif"
            >
              {fractionLabel}
            </text>
          </svg>
        )}
      </div>
    </div>
  );
};
